import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sparkles, RefreshCw, Loader2, ExternalLink, Newspaper, TrendingUp, Users, DollarSign, Handshake, Package, AlertTriangle } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from '@/components/ui/use-toast';
import { format } from 'date-fns';

interface CompanyNewsTabProps {
  companyId: string;
  companyName: string;
}

interface NewsItem {
  id: string;
  title: string;
  summary: string | null;
  source_url: string | null;
  news_type: string;
  published_at: string | null;
  discovered_at: string;
  is_read: boolean;
  relevance_score: number | null;
}

const newsTypeConfig: Record<string, { icon: any; color: string; label: string }> = {
  news: { icon: Newspaper, color: 'bg-blue-500/10 text-blue-600', label: 'News' },
  funding: { icon: DollarSign, color: 'bg-green-500/10 text-green-600', label: 'Funding' },
  acquisition: { icon: TrendingUp, color: 'bg-purple-500/10 text-purple-600', label: 'Acquisition' },
  leadership: { icon: Users, color: 'bg-orange-500/10 text-orange-600', label: 'Leadership' },
  product: { icon: Package, color: 'bg-cyan-500/10 text-cyan-600', label: 'Product' },
  layoffs: { icon: AlertTriangle, color: 'bg-red-500/10 text-red-600', label: 'Layoffs' },
  hiring: { icon: Users, color: 'bg-emerald-500/10 text-emerald-600', label: 'Hiring' },
  partnership: { icon: Handshake, color: 'bg-indigo-500/10 text-indigo-600', label: 'Partnership' },
  financial: { icon: TrendingUp, color: 'bg-amber-500/10 text-amber-600', label: 'Financial' },
};

export function CompanyNewsTab({ companyId, companyName }: CompanyNewsTabProps) {
  const [news, setNews] = useState<NewsItem[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isScanning, setIsScanning] = useState(false);

  const fetchNews = async () => {
    setIsLoading(true);
    try {
      const { data, error } = await supabase
        .from('company_news')
        .select('*')
        .eq('company_id', companyId)
        .order('discovered_at', { ascending: false })
        .limit(50);

      if (error) throw error;
      setNews(data || []);
    } catch (error) {
      console.error('Error fetching news:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchNews();
  }, [companyId]);

  const handleScanNews = async () => {
    setIsScanning(true);
    try {
      const { data, error } = await supabase.functions.invoke('scan-company-news', {
        body: { companyId, companyName }
      });

      if (error) throw error;

      if (data.success) {
        toast({
          title: 'News Scan Complete',
          description: `Found ${data.newsItemsAdded} new items for ${companyName}`,
        });
        fetchNews();
      } else {
        throw new Error(data.error || 'Scan failed');
      }
    } catch (error) {
      console.error('News scan error:', error);
      toast({
        title: 'Scan Failed',
        description: error instanceof Error ? error.message : 'Failed to scan for news',
        variant: 'destructive',
      });
    } finally {
      setIsScanning(false);
    }
  };

  const markAsRead = async (newsId: string) => {
    await supabase
      .from('company_news')
      .update({ is_read: true })
      .eq('id', newsId);
    
    setNews(prev => prev.map(n => n.id === newsId ? { ...n, is_read: true } : n));
  };

  const getTypeConfig = (type: string) => {
    return newsTypeConfig[type] || newsTypeConfig.news;
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">News & Signals</h3>
        <Button
          variant="outline"
          size="sm"
          onClick={handleScanNews}
          disabled={isScanning}
          className="gap-1.5"
        >
          {isScanning ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          {isScanning ? 'Scanning...' : 'Scan for News'}
        </Button>
      </div>

      {news.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            <Sparkles className="h-12 w-12 text-muted-foreground/50 mb-4" />
            <h4 className="font-medium text-muted-foreground mb-2">No news yet</h4>
            <p className="text-sm text-muted-foreground mb-4">
              Click "Scan for News" to find recent news and signals about {companyName}
            </p>
            <Button variant="default" size="sm" onClick={handleScanNews} disabled={isScanning}>
              {isScanning ? 'Scanning...' : 'Scan Now'}
            </Button>
          </CardContent>
        </Card>
      ) : (
        <ScrollArea className="h-[500px]">
          <div className="space-y-3 pr-4">
            {news.map((item) => {
              const config = getTypeConfig(item.news_type);
              const Icon = config.icon;
              
              return (
                <Card 
                  key={item.id} 
                  className={`transition-colors ${!item.is_read ? 'border-primary/30 bg-primary/5' : ''}`}
                  onClick={() => !item.is_read && markAsRead(item.id)}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${config.color}`}>
                        <Icon className="h-4 w-4" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2">
                          <h4 className="font-medium text-sm leading-tight">{item.title}</h4>
                          {!item.is_read && (
                            <Badge variant="secondary" className="text-xs shrink-0">New</Badge>
                          )}
                        </div>
                        {item.summary && (
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {item.summary}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                          <Badge variant="outline" className="text-xs">
                            {config.label}
                          </Badge>
                          {item.published_at && (
                            <span>{format(new Date(item.published_at), 'MMM d, yyyy')}</span>
                          )}
                          {item.source_url && (
                            <a
                              href={item.source_url}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-1 text-primary hover:underline"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <ExternalLink className="h-3 w-3" />
                              Source
                            </a>
                          )}
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </ScrollArea>
      )}
    </div>
  );
}
